import PageHeader from "@/components/feature/PageHeader";
import FeedbackForm from "./components/FeedbackForm";
import { site } from "@/mocks/site";
import { episodes } from "@/mocks/episodes";
import { characters } from "@/mocks/characters";
import { news } from "@/mocks/news";
import useDocumentMeta from "@/hooks/useDocumentMeta";

export default function About() {
  useDocumentMeta({
    title: "关于本站 - 美国禁忌4 影视专题内容站",
    description:
      "了解美国禁忌4影视专题内容站的定位、内容范围与联系方式，查看版权说明与免责声明。本站为纯内容展示型站点。",
  });

  const scope = [
    { icon: "ri-film-line", title: "剧集章节资料", text: `整理全四季共 ${episodes.length} 集的剧情简介、播出时间与关键解析。` },
    { icon: "ri-user-star-line", title: "角色人物档案", text: `收录 ${characters.length} 位核心角色的背景故事、属性与人物关系网络。` },
    { icon: "ri-newspaper-line", title: "资讯与专题解读", text: `汇集 ${news.length} 篇剧集动态、官方消息与深度分析内容。` },
    { icon: "ri-search-eye-line", title: "全站分类检索", text: "支持按关键词、季节、角色类型与资讯类别快速定位内容。" },
  ];

  const notIncluded = [
    "本站不提供任何剧集的播放、在线观看或下载服务。",
    "本站不设会员体系，无需注册与登录即可浏览全部内容。",
    "站内所有剧情介绍、角色资料与资讯均为虚构示例内容。",
  ];

  return (
    <div className="px-4 md:px-10 py-6 md:py-10">
      <PageHeader
        crumbs={[{ label: "首页", to: "/" }, { label: "关于本站" }]}
        eyebrow="About"
        title="关于本站"
        description={`${site.name}是一个以电视剧集为主题的影视专题内容站，致力于为剧迷提供系统、清晰、易读的剧集资料与资讯整理。`}
      />

      {/* Intro */}
      <section className="rounded-lg bg-background-100 border border-background-200 p-6 md:p-8">
        <h2 className="font-heading text-xl md:text-2xl text-foreground-950 tracking-wide mb-4">网站定位</h2>
        <p className="text-[15px] leading-8 text-foreground-700">{site.intro}</p>
        <p className="mt-4 text-[15px] leading-8 text-foreground-700">
          我们面向喜爱悬疑剧集与美剧文化的观众，希望通过结构化的内容编排，让每一个追剧的人都能快速理解
          <strong className="text-foreground-950">剧情脉络</strong>、
          <strong className="text-foreground-950">人物关系</strong>与
          <strong className="text-foreground-950">最新动态</strong>，而不必在零散的信息中反复拼凑。
        </p>
      </section>

      {/* Scope */}
      <section className="mt-10">
        <div className="flex items-center gap-2 mb-5">
          <span className="w-6 h-[2px] bg-accent-500 inline-block" />
          <h2 className="font-heading text-xl md:text-2xl text-foreground-950 tracking-wide">内容范围</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
          {scope.map((item) => (
            <div key={item.title} className="rounded-lg bg-background-100 border border-background-200 p-5">
              <span className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-500/12 text-accent-400 mb-4">
                <i className={`${item.icon} text-xl`} />
              </span>
              <h3 className="font-heading text-base text-foreground-950">{item.title}</h3>
              <p className="mt-2 text-[13px] text-foreground-500 leading-relaxed">{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Contact + form */}
      <section className="mt-10">
        <div className="flex items-center gap-2 mb-5">
          <span className="w-6 h-[2px] bg-accent-500 inline-block" />
          <h2 className="font-heading text-xl md:text-2xl text-foreground-950 tracking-wide">联系方式与建议反馈</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-6 md:gap-8">
          <div className="rounded-lg bg-background-100 border border-background-200 p-6">
            <h3 className="font-heading text-base text-foreground-950 mb-4">联系信息</h3>
            <ul className="space-y-4 text-sm">
              <li className="flex items-center gap-3 text-foreground-700">
                <span className="w-9 h-9 flex items-center justify-center rounded-md bg-background-200 text-accent-400">
                  <i className="ri-mail-line" />
                </span>
                {site.contactEmail}
              </li>
              <li className="flex items-center gap-3 text-foreground-700">
                <span className="w-9 h-9 flex items-center justify-center rounded-md bg-background-200 text-accent-400">
                  <i className="ri-map-pin-line" />
                </span>
                线上内容站点 · 无实体门店
              </li>
              <li className="flex items-start gap-3 text-foreground-700">
                <span className="w-9 h-9 flex items-center justify-center rounded-md bg-background-200 text-accent-400 shrink-0">
                  <i className="ri-chat-smile-2-line" />
                </span>
                如果你发现内容有误或有想看的专题方向，欢迎通过右侧表单告诉我们。
              </li>
            </ul>
          </div>

          <div className="rounded-lg bg-background-100 border border-background-200 p-6">
            <h3 className="font-heading text-base text-foreground-950 mb-4">建议反馈</h3>
            <FeedbackForm />
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="mt-10">
        <div className="flex items-center gap-2 mb-5">
          <span className="w-6 h-[2px] bg-accent-500 inline-block" />
          <h2 className="font-heading text-xl md:text-2xl text-foreground-950 tracking-wide">版权与免责声明</h2>
        </div>
        <div className="rounded-lg bg-background-100 border border-background-200 p-6 md:p-8">
          <ul className="space-y-3">
            {notIncluded.map((text) => (
              <li key={text} className="flex items-start gap-3 text-[15px] leading-7 text-foreground-700">
                <i className="ri-information-line text-accent-400 mt-1" />
                {text}
              </li>
            ))}
          </ul>
          <p className="mt-6 pt-5 border-t border-background-200 text-sm leading-7 text-foreground-500">
            {site.footerNote}
          </p>
        </div>
      </section>
    </div>
  );
}